import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { BackgroundEffectGroups } from '../../../types';
import { BackgroundEffectsItem } from '..';
import './styles.scss';
import { useSelector } from 'react-redux';
import { selectIsInstitutionOwner } from '../../../../../store/features/system';
import { getIsLogoHorizontalDefault } from '../helpers';
export function backgroundEffectsRadioId(groupKey, effect) {
    const safeName = effect.name
        .replace(/[^\dA-Za-z]+/gu, '-')
        .replace(/^-|-$/gu, '');
    return `background-effects-radio--${groupKey}--${safeName}`;
}
export const BackgroundEffectsList = ({ effects, selectedEffect, onSelectEffect, }) => {
    const intl = useIntl();
    const isInstitutionOwner = useSelector(selectIsInstitutionOwner);
    const groupedEffects = useMemo(() => {
        const groups = [];
        for (const group in BackgroundEffectGroups) {
            const filteredEffects = effects
                .filter((effect) => effect.group === group)
                .sort((effect1, effect2) => effect1.index - effect2.index);
            groups.push({
                key: group,
                effects: filteredEffects,
            });
        }
        return groups;
    }, [effects]);
    const getEffectSpecificProps = (effect) => {
        switch (effect.name) {
            case 'Branded':
                const disabled = getIsLogoHorizontalDefault();
                const ownerTooltip = {
                    title: intl.formatMessage({
                        id: 'backgroundEffects.options.branded.disabled.title',
                    }),
                    description: intl.formatMessage({
                        id: 'backgroundEffects.options.branded.disabled.owner.description',
                    }),
                    linkText: intl.formatMessage({
                        id: 'backgroundEffects.options.branded.disabled.owner.linkText',
                    }),
                    linkHref: intl.formatMessage({
                        id: 'backgroundEffects.options.branded.disabled.owner.linkHref',
                    }),
                };
                const memberTooltip = {
                    title: intl.formatMessage({
                        id: 'backgroundEffects.options.branded.disabled.title',
                    }),
                    description: intl.formatMessage({
                        id: 'backgroundEffects.options.branded.disabled.member.description',
                    }),
                };
                const tooltip = disabled
                    ? isInstitutionOwner
                        ? ownerTooltip
                        : memberTooltip
                    : undefined;
                return {
                    disabled,
                    tooltip,
                };
            default:
                return null;
        }
    };
    const handleRadioGroupKeyDown = (groupKey, groupEffects) => (event) => {
        const { key } = event;
        if (key !== 'ArrowDown' &&
            key !== 'ArrowUp' &&
            key !== 'ArrowLeft' &&
            key !== 'ArrowRight' &&
            key !== 'Home' &&
            key !== 'End') {
            return;
        }
        const enabledEffects = groupEffects.filter((effect) => !Boolean(getEffectSpecificProps(effect)?.disabled));
        if (enabledEffects.length === 0) {
            return;
        }
        event.preventDefault();
        let currentIdx = enabledEffects.findIndex((effect) => effect.name === selectedEffect.name);
        if (currentIdx < 0) {
            currentIdx = 0;
        }
        let nextIdx = currentIdx;
        switch (key) {
            case 'ArrowDown':
            case 'ArrowRight':
                nextIdx = (currentIdx + 1) % enabledEffects.length;
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
                nextIdx =
                    (currentIdx - 1 + enabledEffects.length) % enabledEffects.length;
                break;
            case 'Home':
                nextIdx = 0;
                break;
            case 'End':
                nextIdx = enabledEffects.length - 1;
                break;
            default:
                return;
        }
        const nextEffect = enabledEffects[nextIdx];
        onSelectEffect(nextEffect);
        const radioId = backgroundEffectsRadioId(groupKey, nextEffect);
        requestAnimationFrame(() => {
            const el = document.querySelector(`#${CSS.escape(radioId)}`);
            el?.focus();
        });
    };
    return (<div className="background-effects" data-testid="background-effects__container">
      {groupedEffects.map((item) => (<div key={item.key}>
          <p>
            {intl.formatMessage({
                id: `backgroundEffects.category.${item.key}`,
                defaultMessage: 'Category',
            })}
          </p>
          <ul role="radiogroup" className="background-effects__list" aria-label={intl.formatMessage({
                id: item.key,
                defaultMessage: 'label',
            })} onKeyDown={handleRadioGroupKeyDown(item.key, item.effects)}>
            {(() => {
                const enabledInGroup = item.effects.filter((eff) => !Boolean(getEffectSpecificProps(eff)?.disabled));
                const selectionInThisGroup = enabledInGroup.some((eff) => eff.name === selectedEffect.name);
                const firstEnabledInGroup = enabledInGroup[0];
                return item.effects.map((effect) => {
                    const extra = getEffectSpecificProps(effect);
                    const disabled = Boolean(extra?.disabled);
                    const isTabStop = !disabled &&
                        (selectionInThisGroup === true
                            ? selectedEffect.name === effect.name
                            : firstEnabledInGroup != null &&
                                effect.name === firstEnabledInGroup.name);
                    return (<BackgroundEffectsItem id={backgroundEffectsRadioId(item.key, effect)} key={effect.name} index={effect.index} onSelect={() => onSelectEffect(effect)} selected={selectedEffect.name === effect.name} tabIndex={disabled ? -1 : isTabStop ? 0 : -1} title={effect.metadata.title} label={effect.metadata.label} thumbnail={effect.metadata.thumbnail} icon={effect.metadata.icon} {...extra}/>);
                });
            })()}
          </ul>
        </div>))}
    </div>);
};
